import { CtaForm } from '@pairy/sheard/types';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

import { isCtaModalOpenAtom } from '../../../store/index.atom';
import {
  v2ScrollTargetPageAtom,
  v2ZoomAtom,
} from '../../../store/v2/viewer.atom';
import useFitZoom from '../../../hooks/v2/useFitZoom';
import useScrollCurrentPage from '../../../hooks/v2/useScrollCurrentPage';
import usePinchZoom from '../../../hooks/v2/usePinchZoom';
import useDoubleTapZoom from '../../../hooks/v2/useDoubleTapZoom';
import useAnchoredZoom from '../../../hooks/v2/useAnchoredZoom';
import { EmbedItemType, MessageBoxItemType } from '../../PdfViewer/index.types';
import { V2DocumentType } from '../index.types';
import { quantizeImageSizes } from '../zoom.constants';
import V2PageImage from '../V2PageImage';
import PageAnnotations from '../PageAnnotations';
import SlideFreeBadge from '../../@common/FreeBadge/Slide';

import GateStopper from './GateStopper';
import * as Styled from './index.styles';

interface Props {
  document: V2DocumentType;
  currentPage: number;
  blindPages: number[] | null;
  /** startFrom 게이트가 활성(미제출) 상태인지 */
  isGateActive: boolean;
  ctaFormPage?: number;
  messageBoxItems?: MessageBoxItemType[];
  embedItems?: EmbedItemType[];
  ctaForm?: CtaForm;
  isFormSubmitted?: boolean;
  isMobile: boolean;
  translateKoToEn: (item: string) => string;
}

/**
 * V2 연속 스크롤 보기 모드.
 * - 세로 중앙선 IntersectionObserver가 currentPageAtom을 갱신해
 *   페이지별 체류시간 분석이 V1과 동일하게 동작한다.
 * - startFrom 게이트 활성 시 게이트 페이지까지만 렌더(클램프)하고,
 *   게이트 페이지는 블러 + GateStopper 오버레이로 막는다.
 */
const ScrollViewer = ({
  document,
  currentPage,
  blindPages,
  isGateActive,
  ctaFormPage,
  messageBoxItems,
  embedItems,
  ctaForm,
  isFormSubmitted,
  isMobile,
  translateKoToEn,
}: Props) => {
  const zoom = useAtomValue(v2ZoomAtom);
  const [isCtaModalOpen, setIsCtaModalOpen] = useAtom(isCtaModalOpenAtom);
  const [scrollTargetPage, setScrollTargetPage] = useAtom(
    v2ScrollTargetPageAtom,
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScrollRef = useRef(false);

  const { zoomAtPoint } = useAnchoredZoom({ scrollRef });
  usePinchZoom({
    scrollRef,
    transformTargetRef: scrollContentRef,
    zoomAtPoint,
  });
  useDoubleTapZoom({
    scrollRef,
    transformTargetRef: scrollContentRef,
    zoomAtPoint,
  });

  /* 게이트 활성 시 게이트 페이지까지만 렌더한다 */
  const visiblePages =
    isGateActive && ctaFormPage
      ? document.pages.slice(0, ctaFormPage)
      : document.pages;

  const { fitPageWidth } = useFitZoom({
    originalWidth: document.pages[0]?.originalWidth ?? 1,
    originalHeight: document.pages[0]?.originalHeight ?? 1,
  });
  const renderedWidth = fitPageWidth * zoom;

  const { syncCommittedPage } = useScrollCurrentPage({
    rootRef: scrollRef,
    isProgrammaticScrollRef,
    totalPages: visiblePages.length,
  });

  // eslint-disable-next-line no-undef
  const scrollToPage = (page: number, behavior: ScrollBehavior = 'auto') => {
    const root = scrollRef.current;
    const target = root?.querySelector<HTMLElement>(`[data-page="${page}"]`);
    if (!root || !target) {
      return;
    }

    isProgrammaticScrollRef.current = true;
    target.scrollIntoView({ block: 'start', behavior });
    syncCommittedPage(page);

    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 150);
  };

  /* 마운트 시(모드 전환 포함) 현재 페이지 위치로 복원한다 */
  useEffect(() => {
    if (currentPage > 1) {
      scrollToPage(currentPage);
    }
  }, []);

  /* 썸네일 클릭/페이지 입력 등 외부 이동 요청 처리 */
  useEffect(() => {
    if (scrollTargetPage === null) {
      return;
    }
    scrollToPage(scrollTargetPage);
    setScrollTargetPage(null);
  }, [scrollTargetPage]);

  return (
    <Styled.ScrollArea
      ref={scrollRef}
      isScrollLocked={isCtaModalOpen}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      <Styled.ScrollContent ref={scrollContentRef}>
        {visiblePages.map((pageData) => {
          const aspect = pageData.originalWidth / pageData.originalHeight || 1;
          const pageHeight = renderedWidth / aspect;
          const isGatePage = isGateActive && pageData.page === ctaFormPage;
          const isBlind =
            isGatePage || Boolean(blindPages?.includes(pageData.page));

          return (
            <Styled.PageSlot
              key={pageData.page}
              data-page={pageData.page}
              data-zoom-anchor
              style={{ width: renderedWidth, height: pageHeight }}
            >
              <V2PageImage
                pageData={pageData}
                sizes={quantizeImageSizes(renderedWidth)}
                isBlind={isBlind}
                loading={pageData.page <= 2 ? 'eager' : 'lazy'}
              />
              {/* 가림 페이지도 임베드는 블러 썸네일로 노출한다 (상호작용 요소는 PageAnnotations 내부에서 숨김) */}
              <PageAnnotations
                pageData={pageData}
                renderedWidth={renderedWidth}
                renderedHeight={pageHeight}
                messageBoxItems={messageBoxItems}
                embedItems={embedItems}
                ctaForm={ctaForm}
                isFormSubmitted={isFormSubmitted}
                isBlind={isBlind}
                isMobile={isMobile}
              />
              <SlideFreeBadge
                userSubscriptionPlan={document.plan}
                isMobile={isMobile}
              />
              {isGatePage && (
                <GateStopper
                  openForm={() => setIsCtaModalOpen(true)}
                  translateKoToEn={translateKoToEn}
                />
              )}
            </Styled.PageSlot>
          );
        })}
      </Styled.ScrollContent>
    </Styled.ScrollArea>
  );
};

export default ScrollViewer;
